// src/modals/TeamDetailsModal.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { apiFetch } from '../utils/api';
import { Logger } from '../utils/logger';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  alpha
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import ChatIcon from '@mui/icons-material/Chat';
import CallIcon from '@mui/icons-material/Call';
import BaseExpandedCard from '../components/cards/BaseExpandedCard';
import EmployeeNameLink from '../components/common/EmployeeNameLink';
import { TABS } from '../components/cards/EmployeeExpandedView';

const logger = new Logger('TeamDetailsModal');

const TeamDetailsModal = ({ 
  open, 
  onClose, 
  projectName, 
  projectId,
  clientName = '', 
  team = [] 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [billedHoursMap, setBilledHoursMap] = useState({});
  
  useEffect(() => {
    if (open) {
      // Always fetch from API when we have a projectId — the team prop only has
      // sparse data (name/role/id) without enriched fields (hours, dates, rates).
      // Fall back to the sparse prop data only if the API returns nothing.
      if (!projectId) {
        if (team && team.length > 0) {
          setTeamMembers(team);
        } else {
          setError('No project ID provided');
        }
        return;
      }

      setLoading(true);
      setError(null);

      // Fetch team data from API
      const fetchTeamMembers = async () => {
        try {
          // Format project ID for API call
          const formattedId = projectId.toString();
          const apiProjectId = formattedId.startsWith('P') ? formattedId : `P${formattedId}`;

          // Fetch billed hours from timesheet data in parallel with assignments
          apiFetch(`projects/${apiProjectId}/team-hours`)
            .then(r => r.ok ? r.json() : {})
            .then(map => setBilledHoursMap(map || {}))
            .catch(() => setBilledHoursMap({}));

          logger.debug(`Fetching team details for project: ${apiProjectId}`);
          
          // Fetch assignments
          const response = await apiFetch(`projects/${apiProjectId}/assignments`);
          if (!response.ok) throw new Error(`HTTP error ${response.status}`);
          const responseData = await response.json();

          if (responseData && Array.isArray(responseData)) {
            logger.debug('Raw assignments data:', responseData);
            
            // Group assignments by employee_id
            const employeeMap = new Map();
            
            // First pass - collect assignments by employee
            for (const assignment of responseData) {
              const employeeId = assignment.employee_id;
              if (!employeeId) continue;
              
              // Parse assignment dates
              const startDate = assignment.role_start_date ? 
                new Date(assignment.role_start_date) : null;
              const endDate = assignment.role_end_date ? 
                new Date(assignment.role_end_date) : null;
                
              // Format dates in a consistent way for display
              const formattedStartDate = startDate ? 
                startDate.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'}) : null;
              const formattedEndDate = endDate ? 
                endDate.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'}) : null;
                
              // Format period string
              let periodString = 'Not specified';
              if (formattedStartDate && formattedEndDate) {
                periodString = `${formattedStartDate} - ${formattedEndDate}`;
              } else if (formattedStartDate) {
                periodString = `From ${formattedStartDate}`;
              } else if (formattedEndDate) {
                periodString = `Until ${formattedEndDate}`;
              }
              
              // Calculate assigned hours
              const hoursAssigned = parseFloat(assignment.total_hours_assigned) || 
                (parseFloat(assignment.weekly_hours || 0) * parseFloat(assignment.role_duration_weeks || 0));
              
              if (!employeeMap.has(employeeId)) {
                employeeMap.set(employeeId, {
                  employee_id: employeeId,
                  assignments: [{
                    ...assignment,
                    formattedStartDate,
                    formattedEndDate,
                    periodString,
                    hours: hoursAssigned
                  }],
                  roles: [assignment.role || 'Team Member'],
                  total_hours_assigned: hoursAssigned
                });
              } else {
                const employeeData = employeeMap.get(employeeId);
                
                // Add this assignment
                employeeData.assignments.push({
                  ...assignment,
                  formattedStartDate,
                  formattedEndDate,
                  periodString,
                  hours: hoursAssigned
                });
                
                // Add role if not already included
                const role = assignment.role || 'Team Member';
                if (!employeeData.roles.includes(role)) {
                  employeeData.roles.push(role);
                }
                
                // Add to total assigned hours
                employeeData.total_hours_assigned += hoursAssigned;
              }
            }
            
            // Second pass - fetch employee details and process final team data
            const processedTeam = await Promise.all(Array.from(employeeMap.values()).map(async (employeeInfo) => {
              let employeeData = {};
              
              // Try to fetch employee details
              if (employeeInfo.employee_id) {
                try {
                  const employeeResponse = await apiFetch(`employees/${employeeInfo.employee_id}`);
                  if (employeeResponse.ok) {
                    employeeData = await employeeResponse.json();
                  }
                } catch (err) {
                  logger.warn(`Couldn't fetch employee data for ${employeeInfo.employee_id}:`, err);
                }
              }
              
              // Determine employee name and title
              const firstName = employeeData.first_name || '';
              const lastName = employeeData.last_name || '';
              const fullName = `${firstName} ${lastName}`.trim() || 'Unknown';
              const title = employeeData.title || '';
              
              // Process roles - remove "Team Member" if there are other roles
              let roles = [...employeeInfo.roles];
              if (roles.length > 1 && roles.includes('Team Member')) {
                roles = roles.filter(role => role !== 'Team Member');
              }
              
              // Determine primary assignment status (use most active/current status)
              const assignmentStatuses = employeeInfo.assignments.map(a => a.assignment_status || 'Active');
              let status = 'Active';
              if (assignmentStatuses.includes('Active')) {
                status = 'Active';
              } else if (assignmentStatuses.includes('Soft Booked')) {
                status = 'Soft Booked';
              } else if (assignmentStatuses.includes('Historical')) {
                status = 'Historical';
              }
              
              // Determine if this is a project manager
              const isPM = roles.some(role => role.toLowerCase().includes('project manager'));
              
              // Billed hours come from timesheet data fetched in parallel
              const hoursBilled = null; // resolved from billedHoursMap after render
              
              // Get the earliest start and latest end dates across all assignments
              const allStartDates = employeeInfo.assignments
                .filter(a => a.formattedStartDate)
                .map(a => new Date(a.role_start_date));
                
              const allEndDates = employeeInfo.assignments
                .filter(a => a.formattedEndDate)
                .map(a => new Date(a.role_end_date));
              
              let assignmentPeriod = 'Not specified';
              if (allStartDates.length > 0 && allEndDates.length > 0) {
                const earliestStart = new Date(Math.min(...allStartDates));
                const latestEnd = new Date(Math.max(...allEndDates));
                
                const formattedEarliestStart = earliestStart.toLocaleDateString('en-US', 
                  {month: 'short', day: 'numeric', year: 'numeric'});
                const formattedLatestEnd = latestEnd.toLocaleDateString('en-US', 
                  {month: 'short', day: 'numeric', year: 'numeric'});
                  
                assignmentPeriod = `${formattedEarliestStart} - ${formattedLatestEnd}`;
              } else if (allStartDates.length > 0) {
                const earliestStart = new Date(Math.min(...allStartDates));
                assignmentPeriod = `From ${earliestStart.toLocaleDateString('en-US', 
                  {month: 'short', day: 'numeric', year: 'numeric'})}`;
              } else if (allEndDates.length > 0) {
                const latestEnd = new Date(Math.max(...allEndDates));
                assignmentPeriod = `Until ${latestEnd.toLocaleDateString('en-US', 
                  {month: 'short', day: 'numeric', year: 'numeric'})}`;
              }
              
              // Weighted effective rate: total assignment value / total assigned hours
              const totalAssignValue = employeeInfo.assignments.reduce(
                (s, a) => s + (parseFloat(a.assignment_value) || 0), 0
              );
              const effectiveBillingRate = employeeInfo.total_hours_assigned > 0 && totalAssignValue > 0
                ? totalAssignValue / employeeInfo.total_hours_assigned
                : (parseFloat(employeeInfo.assignments[0]?.billing_rate) || 0);

              return {
                name: fullName,
                employee_id: employeeInfo.employee_id,
                employee_data: employeeData,
                role: roles.join(', '),
                title: title,
                hours_assigned: Math.round(employeeInfo.total_hours_assigned * 10) / 10,
                hours_billed: hoursBilled,
                status: status,
                email: employeeData.email,
                phone: employeeData.phone,
                current_status: employeeData.current_status,
                assignment_period: assignmentPeriod,
                all_assignments: employeeInfo.assignments,
                billing_rate: employeeInfo.assignments[0]?.billing_rate || 0,
                effective_billing_rate: effectiveBillingRate,
                is_project_manager: isPM,
                is_terminated: employeeData.current_status === 'Terminated',
                is_soft_booked: status === 'Soft Booked'
              };
            }));
            
            // Sort: Project Manager first, then alphabetically
            processedTeam.sort((a, b) => {
              if (a.is_project_manager && !b.is_project_manager) return -1;
              if (!a.is_project_manager && b.is_project_manager) return 1;
              return a.name.localeCompare(b.name);
            });

            logger.debug("Processed team members:", processedTeam);
            setTeamMembers(processedTeam);
          } else {
            logger.warn("No team data in response or invalid format");
            // Fall back to sparse team prop if API returned nothing useful
            if (team && team.length > 0) {
              setTeamMembers(team);
            } else {
              setTeamMembers([]);
            }
          }
        } catch (err) {
          logger.error('Error fetching team members:', err);
          setError('Failed to load team members');
        } finally {
          setLoading(false);
        }
      };
      
      fetchTeamMembers();
    }
  }, [open, projectId, team]);
  
  // Communication handler functions
  const handleEmail = (e, email, name) => {
    e.stopPropagation();
    if (email) {
      window.location.href = `mailto:${email}`;
    } else {
      logger.debug(`No email available for ${name}`);
    }
  };
  
  const handleChat = (e, employeeId, name) => {
    e.stopPropagation();
    if (employeeId) {
      // Construct Teams chat URL with employee email
      const email = `${employeeId.toLowerCase()}@mbco.com`;
      window.open(`https://teams.microsoft.com/l/chat/0/0?users=${email}`, '_blank');
    } else {
      logger.debug(`Cannot chat with ${name} - no employee ID`);
    }
  };
  
  const handleCall = (e, phone, name) => {
    e.stopPropagation();
    if (phone) {
      window.location.href = `tel:${phone}`;
    } else {
      logger.debug(`No phone number available for ${name}`);
    }
  };
  
  return (
    <BaseExpandedCard
      open={open}
      onClose={onClose}
      title={
        <Typography variant="h5">
          Team Details
        </Typography>
      }
      subtitle={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {clientName && (
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: '#03DAC6', 
                fontSize: '1rem', 
                fontWeight: 'medium'
              }}
            >
              {clientName}
            </Typography>
          )}
          {clientName && projectName && (
            <Typography 
              variant="subtitle1" 
              sx={{ mx: 1, color: 'text.secondary' }}
            >
              |
            </Typography>
          )}
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontSize: '1rem',
              fontWeight: 'medium'
            }}
          >
            {projectName || ''}
          </Typography>
        </Box>
      }
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ p: 2 }}>
          {error}
        </Typography>
      ) : (
        <Box sx={{ p: 1.5 }}>
          {teamMembers.length > 0 ? (
            teamMembers.map((member, index) => (
              <Box
                key={index}
                sx={{
                  mb: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: '#1E1E1E',
                  border: '1px solid rgba(255,255,255,0.06)'
                }}
              >
                {/* Name / title / contact row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#64B5F6' }}>
                        <EmployeeNameLink employee={member} tab={TABS.ENGAGEMENTS}>{member.name}</EmployeeNameLink>
                      </Typography>
                      {member.title && (
                        <Typography sx={{ fontSize: '0.75rem', color: '#CF6679' }}>
                          {member.title}
                        </Typography>
                      )}
                    </Box>

                    {member.employee_id && (
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 0.25 }}>
                        {member.employee_id}
                      </Typography>
                    )}

                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 }}>
                      <Box component="span" sx={{ color: 'text.disabled' }}>Role: </Box>
                      {member.role}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                    {member.is_terminated ? (
                      <Chip
                        label="Terminated"
                        size="small"
                        sx={{ bgcolor: alpha('#CF6679', 0.15), color: '#CF6679', fontSize: '0.6rem', height: 20 }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.25 }}>
                        <Tooltip title={member.email ? `Email ${member.name.split(' ')[0]}` : 'No email'}>
                          <IconButton size="small" sx={{ color: member.email ? '#64B5F6' : 'rgba(100,181,246,0.4)', p: 0.4 }}
                            onClick={(e) => handleEmail(e, member.email, member.name)}>
                            <EmailIcon sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={`Chat with ${member.name.split(' ')[0]}`}>
                          <IconButton size="small" sx={{ color: '#BB86FC', p: 0.4 }}
                            onClick={(e) => handleChat(e, member.employee_id, member.name)}>
                            <ChatIcon sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={member.phone ? `Call ${member.name.split(' ')[0]}` : 'No phone'}>
                          <IconButton size="small" sx={{ color: member.phone ? '#03DAC6' : 'rgba(3,218,198,0.4)', p: 0.4 }}
                            onClick={(e) => handleCall(e, member.phone, member.name)}>
                            <CallIcon sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    <Chip
                      label={member.status || 'Unknown'}
                      size="small"
                      sx={{
                        fontSize: '0.6rem', height: 20,
                        bgcolor: member.status === 'Active' ? 'rgba(3,218,198,0.1)' :
                                 member.status === 'Soft Booked' ? 'rgba(255,207,68,0.1)' :
                                 'rgba(255,255,255,0.08)',
                        color: member.status === 'Active' ? '#03DAC6' :
                               member.status === 'Soft Booked' ? '#FFCF44' :
                               member.status === 'Historical' ? '#AAAAAA' : '#CF6679'
                      }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.06)' }} />

                {/* Stats row */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {/* Assignment Period */}
                  <Box>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.25 }}>
                      Period
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.primary' }}>
                      {member.assignment_period}
                    </Typography>
                  </Box>

                  {/* Hours */}
                  <Box>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.25 }}>
                      Hours
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary' }}>
                          {member.hours_assigned}
                        </Typography>
                        <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>assigned</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        {member.is_soft_booked ? (
                          <>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#FFCF44' }}>—</Typography>
                            <Typography sx={{ fontSize: '0.6rem', color: '#FFCF44' }}>soft booked</Typography>
                          </>
                        ) : (() => {
                          const billed = billedHoursMap[member.employee_id];
                          const isOver = billed !== undefined && billed > member.hours_assigned;
                          return (
                            <>
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: billed === undefined ? 'text.disabled' : isOver ? '#CF6679' : '#03DAC6' }}>
                                {billed !== undefined ? billed : '—'}
                              </Typography>
                              <Typography sx={{ fontSize: '0.6rem', color: isOver ? '#CF6679' : 'text.disabled' }}>
                                {isOver ? 'over' : 'billed'}
                              </Typography>
                            </>
                          );
                        })()}
                      </Box>
                      {member.all_assignments?.[0]?.weekly_hours && (
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary' }}>
                            {member.all_assignments[0].weekly_hours}
                          </Typography>
                          <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>hrs/wk</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Billing rate */}
                  {member.billing_rate > 0 && (
                    <Box>
                      <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.25 }}>
                        Rate
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.primary' }}>
                        ${member.billing_rate}/hr
                      </Typography>
                    </Box>
                  )}

                  {/* Amount Billed */}
                  {member.effective_billing_rate > 0 && (() => {
                    const billedHrs = billedHoursMap[member.employee_id] || 0;
                    const amountBilled = billedHrs * member.effective_billing_rate;
                    return (
                      <Box>
                        <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.25 }}>
                          Amount Billed
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#BB86FC' }}>
                          ${Math.round(amountBilled).toLocaleString()}
                        </Typography>
                      </Box>
                    );
                  })()}
                </Box>

                {/* Multiple assignments detail */}
                {member.all_assignments && member.all_assignments.length > 1 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                      Assignment Details
                    </Typography>
                    {member.all_assignments.map((assignment, idx) => (
                      <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4,
                        borderBottom: idx < member.all_assignments.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                          {assignment.role || 'Team Member'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>{assignment.periodString}</Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>{assignment.hours}h</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))
          ) : (
            <Typography sx={{ fontSize: '0.75rem', textAlign: 'center', py: 3, color: 'text.secondary' }}>
              No team members found for this project
            </Typography>
          )}

          {/* Totals row */}
          {teamMembers.length > 0 && (() => {
            const totalAssigned = teamMembers.reduce((s, m) => s + (m.hours_assigned || 0), 0);
            const totalBilled   = teamMembers.reduce((s, m) => s + (billedHoursMap[m.employee_id] || 0), 0);
            const totalAmount   = teamMembers.reduce((s, m) => {
              const billedHrs = billedHoursMap[m.employee_id] || 0;
              return s + billedHrs * (m.effective_billing_rate || 0);
            }, 0);
            return (
              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                  Project Totals
                </Typography>
                <Box sx={{ display: 'flex', gap: 4 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                      {Math.round(totalAssigned * 10) / 10}h
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>Hours Assigned</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#03DAC6' }}>
                      {Math.round(totalBilled * 10) / 10}h
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>Hours Billed</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#BB86FC' }}>
                      ${Math.round(totalAmount).toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>Amount Billed</Typography>
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </Box>
      )}
    </BaseExpandedCard>
  );
};

TeamDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectName: PropTypes.string,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  clientName: PropTypes.string,
  team: PropTypes.array
};

export default TeamDetailsModal;
